<script setup lang="ts">
import { computed } from 'vue';
import { IconBook } from '@/components/design/icons';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaSpinner from '@/components/design/base/GaiaSpinner.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTooltip from '@/components/design/base/GaiaTooltip.vue';
import {
  gaiaDiff,
  gaiaFonts,
  gaiaFontSize,
  gaiaFontWeight,
  gaiaNeutral,
} from '@/components/design/themes/tokens';
import type { DiffLine } from './VaultFileRevisionDiffView.vue';

const props = defineProps<{
  parsedLines: DiffLine[];
  addedLines: number;
  removedLines: number;
  loading: boolean;
  error: string;
  uploadedAt: string | null;
  changeDescription: string | null;
  uploaderEmail: string | null;
}>();

const emit = defineEmits<{
  navigatePage: [line: DiffLine];
}>();

/** Every remaining row is a page separator, so there is nothing to read. */
const isEmpty = computed(
  () => !props.loading && !props.error && props.parsedLines.every(line => line.type === 'page')
);

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const headBorder = gaiaNeutral.borderMuted;
const rule = gaiaNeutral.border;
const surface = gaiaNeutral.surface;
const contextText = gaiaNeutral.text;
const labelColor = gaiaNeutral.textSoft;
const monoFont = gaiaFonts.mono;
const countSize = gaiaFontSize.micro;
const countWeight = String(gaiaFontWeight.bold);
const whoWeight = String(gaiaFontWeight.semibold);
const lineSize = gaiaFontSize.micro;
const badgeSize = gaiaFontSize.nano;
const addedText = gaiaDiff.addedText;
const addedSurface = gaiaDiff.addedSurface;
const addedStrong = gaiaDiff.addedStrong;
const addedBadge = gaiaDiff.addedBadgeSurface;
const removedText = gaiaDiff.removedText;
const removedSurface = gaiaDiff.removedSurface;
const removedStrong = gaiaDiff.removedStrong;
const removedBadge = gaiaDiff.removedBadgeSurface;
</script>

<template>
  <div class="diff-panel">
    <!-- Counts, then who put this version there. The two numbers are the whole
         answer to "how much changed", so they lead. -->
    <div class="head">
      <span class="count added">+{{ props.addedLines }}</span>
      <span class="count removed">−{{ props.removedLines }}</span>
      <gaia-text v-if="props.uploadedAt" truncate size="control" tone="tertiary">
        <template v-if="props.uploaderEmail">
          <strong class="who">{{ props.uploaderEmail }}</strong>
          uploaded on {{ formatDate(props.uploadedAt) }}
        </template>
        <template v-else>Uploaded {{ formatDate(props.uploadedAt) }}</template>
        <template v-if="props.changeDescription"> · “{{ props.changeDescription }}”</template>
      </gaia-text>
    </div>

    <div class="body">
      <n-flex v-if="props.loading" align="center" justify="center" :size="10" class="state">
        <gaia-spinner size="small" />
        <gaia-text size="control" tone="tertiary">Loading diff…</gaia-text>
      </n-flex>

      <gaia-empty-state v-else-if="props.error" status="alert" title="Couldn’t load the comparison">
        {{ props.error }}
      </gaia-empty-state>

      <gaia-empty-state v-else-if="isEmpty" title="No text differences found">
        The two versions read the same. They may still differ in formatting, images or metadata —
        this compares extracted text only.
      </gaia-empty-state>

      <template v-else>
        <template v-for="(line, index) in props.parsedLines" :key="index">
          <!-- A page break is a rule with its number set into it, and it is the
               control that drives both PDFs to that page. -->
          <gaia-tooltip v-if="line.type === 'page'" placement="right" :delay="300">
            <template #trigger>
              <div class="page-sep" @click="emit('navigatePage', line)">
                <span class="rule" />
                <span class="page-label">
                  <n-icon :component="IconBook" :size="12" />
                  Page {{ line.pageNumber }}
                  <span v-if="line.pagePrefix === '+'" class="badge added">new</span>
                  <span v-else-if="line.pagePrefix === '-'" class="badge removed">removed</span>
                </span>
                <span class="rule" />
              </div>
            </template>
            Jump both PDFs to page {{ line.pageNumber }}
          </gaia-tooltip>

          <pre v-else class="line" :class="line.type">{{ line.text }}</pre>
        </template>
      </template>
    </div>
  </div>
</template>

<style scoped>
.diff-panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  background: v-bind(surface);
}

.head {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: none;
  height: 40px;
  padding: 0 14px;
  overflow: hidden;
  border-bottom: 1px solid v-bind(headBorder);
}

/* Tabular so the two counts keep one width as the reader pages through. */
.count {
  flex: none;
  font-family: v-bind(monoFont);
  font-size: v-bind(countSize);
  font-weight: v-bind(countWeight);
  font-variant-numeric: tabular-nums;
}

.count.added {
  color: v-bind(addedStrong);
}

.count.removed {
  color: v-bind(removedStrong);
}

.who {
  font-weight: v-bind(whoWeight);
  color: v-bind(contextText);
}

.body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 8px 0;
}

.state {
  padding: 40px 0;
}

/* Wraps rather than scrolls sideways: this pane is a third of the width in the
   side-by-side layout, and a horizontal scrollbar there means every long line
   has to be dragged into view one at a time. */
.line {
  margin: 0;
  padding: 1px 14px;
  font-family: v-bind(monoFont);
  font-size: v-bind(lineSize);
  line-height: 1.75;
  white-space: pre-wrap;
  word-break: break-word;
  color: v-bind(contextText);
}

.line.added {
  background: v-bind(addedSurface);
  color: v-bind(addedText);
}

.line.removed {
  background: v-bind(removedSurface);
  color: v-bind(removedText);
}

.page-sep {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 6px 0;
  cursor: pointer;
}

.page-sep:hover {
  opacity: 0.75;
}

.rule {
  flex: 1;
  height: 1px;
  background: v-bind(rule);
}

.page-label {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: none;
  font-family: v-bind(monoFont);
  font-size: v-bind(lineSize);
  color: v-bind(labelColor);
}

.badge {
  display: inline-flex;
  align-items: center;
  height: 16px;
  padding: 0 6px;
  border-radius: 8px;
  font-family: inherit;
  font-size: v-bind(badgeSize);
  font-weight: v-bind(countWeight);
}

.badge.added {
  background: v-bind(addedBadge);
  color: v-bind(addedStrong);
}

.badge.removed {
  background: v-bind(removedBadge);
  color: v-bind(removedStrong);
}
</style>
